/*
 * kemi-motion.js — animaciones de entrada nativas para la landing Kemi, sin GSAP.
 * Reemplaza AnimatedHeading / Reveal / CountUp del proyecto Next.js usando
 * IntersectionObserver + requestAnimationFrame. Custom elements auto-registrados;
 * las definiciones se guardan para poder cargar el módulo desde varias secciones.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Observer compartido: dispara `is-in` cuando el elemento entra en viewport. */
const revealObserver = new IntersectionObserver(
  (entries, obs) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      entry.target.dispatchEvent(new CustomEvent('kemi:in'));
      obs.unobserve(entry.target);
    }
  },
  { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
);

/**
 * <kemi-reveal> — sube y aparece al entrar en viewport. Con `stagger`, escalona a
 * sus hijos directos aplicando un --kemi-delay incremental.
 */
class KemiReveal extends HTMLElement {
  connectedCallback() {
    const delay = parseFloat(this.getAttribute('delay') || '0');
    const stagger = parseFloat(this.getAttribute('stagger') || '0');

    if (stagger > 0) {
      Array.from(this.children).forEach((child, i) => {
        child.classList.add('kemi-reveal');
        child.style.setProperty('--kemi-delay', `${delay + i * stagger}s`);
      });
    } else {
      this.classList.add('kemi-reveal');
      if (delay) this.style.setProperty('--kemi-delay', `${delay}s`);
    }

    if (REDUCED) {
      this.classList.add('is-in');
      this.querySelectorAll('.kemi-reveal').forEach((el) => el.classList.add('is-in'));
      return;
    }

    if (stagger > 0) {
      // Observamos el contenedor; al entrar, marcamos todos los hijos.
      const once = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.querySelectorAll('.kemi-reveal').forEach((el) => el.classList.add('is-in'));
          obs.disconnect();
        }
      }, { threshold: 0.15 });
      once.observe(this);
    } else {
      revealObserver.observe(this);
    }
  }
}

/**
 * <kemi-heading> — envuelve cada palabra del texto para revelarla desde abajo con
 * stagger al entrar en viewport. Marca `.kemi-accent` las palabras con [data-accent].
 * El markup de palabras lo genera el servidor (Liquid) o, si no, este constructor.
 */
class KemiHeading extends HTMLElement {
  connectedCallback() {
    this.classList.add('kemi-heading');
    const words = this.querySelectorAll('.kemi-word');
    const base = parseFloat(this.getAttribute('delay') || '0');
    const step = parseFloat(this.getAttribute('stagger') || '0.06');

    words.forEach((w, i) => {
      const inner = w.querySelector('span');
      if (inner) inner.style.setProperty('--kemi-delay', `${base + i * step}s`);
    });

    if (REDUCED) { this.classList.add('is-in'); return; }
    revealObserver.observe(this);
  }
}

/**
 * <kemi-countup> — cuenta de 0 al valor `to` cuando entra en viewport, con easing.
 * `suffix` se concatena al número (ej. " g"). Respeta reduced-motion (muestra final).
 */
class KemiCountUp extends HTMLElement {
  connectedCallback() {
    const to = parseFloat(this.getAttribute('to') || '0');
    const suffix = this.getAttribute('suffix') || '';
    const duration = parseFloat(this.getAttribute('duration') || '1.6') * 1000;
    const render = (n) => { this.textContent = `${Math.round(n)}${suffix}`; };

    render(0);
    if (REDUCED) { render(to); return; }

    const once = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        obs.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
          render(to * eased);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    once.observe(this);
  }
}

/**
 * <kemi-qty> — stepper de cantidad (+/−) que actualiza un <input name="quantity">
 * dentro del form de producto nativo. Réplica del QuantityStepper original.
 */
class KemiQty extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input');
    if (!input) return;
    this.querySelectorAll('[data-dir]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = parseInt(btn.getAttribute('data-dir'), 10);
        const min = parseInt(input.min || '1', 10);
        let v = (parseInt(input.value || '1', 10) || min) + dir;
        if (v < min) v = min;
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

if (!customElements.get('kemi-qty')) customElements.define('kemi-qty', KemiQty);
if (!customElements.get('kemi-reveal')) customElements.define('kemi-reveal', KemiReveal);
if (!customElements.get('kemi-heading')) customElements.define('kemi-heading', KemiHeading);
if (!customElements.get('kemi-countup')) customElements.define('kemi-countup', KemiCountUp);
